import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { supabase } from '@/lib/supabase';

function formatBRL(value: number): string {
  return value.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
}

function formatDate(iso: string): string {
  if (!iso) return '-';
  return new Date(iso).toLocaleDateString('pt-BR', {
    day: '2-digit', month: '2-digit', year: 'numeric',
  });
}

function formatCPF(value: string): string {
  const digits = value.replace(/\D/g, '');
  if (digits.length <= 11) {
    return digits.replace(/(\d{3})(\d)/, '$1.$2')
      .replace(/(\d{3})(\d)/, '$1.$2')
      .replace(/(\d{3})(\d{1,2})$/, '$1-$2');
  }
  return digits.replace(/(\d{2})(\d)/, '$1.$2')
    .replace(/(\d{3})(\d)/, '$1.$2')
    .replace(/(\d{3})(\d)/, '$1/$2')
    .replace(/(\d{4})(\d{1,2})$/, '$1-$2');
}

const STATUS_LABELS: Record<string, string> = {
  negociacao: 'Em negociação',
  fechado: 'Fechado',
  pago: 'Pago',
  entregue: 'Entregue',
  cancelado: 'Cancelado',
};

export default function ContratoPage() {
  const { orderId } = useParams<{ orderId: string }>();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [order, setOrder] = useState<any>(null);
  const [contractHtml, setContractHtml] = useState('');
  const [contractContent, setContractContent] = useState('');
  const [contractStatus, setContractStatus] = useState<string | null>(null);
  const [acceptedAt, setAcceptedAt] = useState<string | null>(null);
  const [acceptedByCpf, setAcceptedByCpf] = useState<string | null>(null);
  const [acceptedByName, setAcceptedByName] = useState<string | null>(null);

  const [cpf, setCpf] = useState('');
  const [name, setName] = useState('');
  const [accepted, setAccepted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [submitSuccess, setSubmitSuccess] = useState(false);

  useEffect(() => {
    if (!orderId) return;
    loadContract();
  }, [orderId]);

  const loadContract = async () => {
    setLoading(true);
    setError(null);
    try {
      const { data, error: invokeErr } = await supabase.functions.invoke('contract-public', {
        body: { action: 'get_contract', order_id: Number(orderId) },
      });
      if (invokeErr) {
        const msg = (invokeErr as any)?.message ?? 'Erro ao carregar contrato.';
        setError(msg);
        return;
      }
      const parsed = data as {
        ok?: boolean;
        error?: string;
        order?: any;
        contract_html?: string;
        contract_status?: string | null;
        accepted_at?: string | null;
        accepted_by_cpf?: string | null;
        accepted_by_name?: string | null;
      };
      if (parsed?.error) {
        setError(parsed.error);
        return;
      }
      if (!parsed?.ok) {
        setError('Falha ao carregar contrato.');
        return;
      }
      setOrder(parsed.order);
      setContractHtml(parsed.contract_html ?? '');
      setContractStatus(parsed.contract_status ?? null);
      // Busca o conteudo customizado do contrato (gerado por IA no painel)
      const { data: contractRow } = await supabase
        .from('order_contracts')
        .select('contract_content')
        .eq('order_id', Number(orderId))
        .maybeSingle();
      if (contractRow?.contract_content) {
        setContractContent(contractRow.contract_content);
      }
      setAcceptedAt(parsed.accepted_at ?? null);
      setAcceptedByCpf(parsed.accepted_by_cpf ?? null);
      setAcceptedByName(parsed.accepted_by_name ?? null);
    } catch (e: any) {
      setError(e?.message ?? 'Erro ao carregar contrato.');
    } finally {
      setLoading(false);
    }
  };

  const handleAccept = async () => {
    setSubmitError(null);
    if (!accepted) {
      setSubmitError('Você precisa marcar que leu e aceita as cláusulas.');
      return;
    }
    const cleanName = name.trim();
    if (!cleanName) {
      setSubmitError('Informe seu nome completo.');
      return;
    }
    const cleanCpf = cpf.replace(/\D/g, '');
    if (cleanCpf.length < 11) {
      setSubmitError('Informe um CPF/CNPJ válido.');
      return;
    }
    setSubmitting(true);
    try {
      const { data, error: invokeErr } = await supabase.functions.invoke('contract-public', {
        body: { action: 'accept_contract', order_id: Number(orderId), cpf: cleanCpf, name: cleanName },
      });
      if (invokeErr) {
        const msg = (invokeErr as any)?.message ?? 'Erro ao aceitar contrato.';
        setSubmitError(msg);
        return;
      }
      const parsed = data as { ok?: boolean; error?: string; message?: string };
      if (parsed?.error) {
        setSubmitError(parsed.error);
        return;
      }
      if (!parsed?.ok) {
        setSubmitError('Falha ao aceitar contrato.');
        return;
      }
      setSubmitSuccess(true);
      setContractStatus('accepted');
      setAcceptedAt(new Date().toISOString());
      setAcceptedByCpf(cleanCpf);
      setAcceptedByName(cleanName);
    } catch (e: any) {
      setSubmitError(e?.message ?? 'Erro ao aceitar contrato.');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#f6f6f4] flex items-center justify-center">
        <div className="flex flex-col items-center gap-3">
          <div className="w-10 h-10 border-2 border-[#E8610A] border-t-transparent rounded-full animate-spin" />
          <p className="text-sm text-gray-500">Carregando contrato...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-[#f6f6f4] flex items-center justify-center px-4">
        <div className="bg-white rounded-2xl border border-red-200 p-8 max-w-md w-full text-center">
          <div className="w-12 h-12 flex items-center justify-center bg-red-50 text-red-600 rounded-xl mx-auto mb-4">
            <i className="ri-error-warning-line text-xl"></i>
          </div>
          <h2 className="text-lg font-bold text-gray-900 mb-2">Contrato não encontrado</h2>
          <p className="text-sm text-gray-500">{error}</p>
        </div>
      </div>
    );
  }

  if (submitSuccess || contractStatus === 'accepted') {
    return (
      <div className="min-h-screen bg-[#f6f6f4] py-12 px-4">
        <div className="max-w-xl mx-auto bg-white rounded-2xl border border-green-200 p-8 text-center">
          <div className="w-16 h-16 flex items-center justify-center bg-green-50 text-green-600 rounded-full mx-auto mb-4">
            <i className="ri-check-line text-3xl"></i>
          </div>
          <h1 className="text-2xl font-bold text-gray-900 mb-2">Contrato Aceito!</h1>
          <p className="text-gray-600 mb-4">
            O contrato do pedido nº <strong>{order?.id}</strong> foi aceito com sucesso.
          </p>
          {acceptedAt && (
            <p className="text-sm text-gray-500">
              Aceito em: {formatDate(acceptedAt)} às{' '}
              {new Date(acceptedAt).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}
            </p>
          )}
          {acceptedByName && (
            <p className="text-sm text-gray-500 mt-1">
              Signatário: {acceptedByName}
            </p>
          )}
          {acceptedByCpf && (
            <p className="text-sm text-gray-500 mt-1">
              CPF/CNPJ confirmado: {formatCPF(acceptedByCpf)}
            </p>
          )}
          <div className="mt-6 pt-6 border-t border-gray-100">
            <p className="text-sm text-gray-500">
              Em breve nossa equipe entrará em contato para prosseguir com a produção.
            </p>
            <a
              href="https://homeofficepremium.com"
              className="inline-flex items-center gap-2 mt-4 text-[#E8610A] hover:text-[#d45508] font-semibold text-sm transition-colors"
            >
              <i className="ri-home-line"></i>
              Voltar para o site
            </a>
          </div>
        </div>
      </div>
    );
  }

  const recipient = order?.recipient ?? {};
  const items = order?.order_items ?? [];
  const total = Number(order?.subtotal_items ?? 0);
  const statusLabel = STATUS_LABELS[order?.status] ?? order?.status;

  return (
    <div className="min-h-screen bg-[#f6f6f4] py-8 px-4">
      <div className="max-w-[800px] mx-auto">
        {/* Header */}
        <div className="bg-white rounded-2xl border border-gray-200 p-6 mb-4 flex items-center justify-between flex-wrap gap-3">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 flex items-center justify-center bg-[#E8610A]/10 text-[#E8610A] rounded-xl">
              <i className="ri-file-text-line text-lg"></i>
            </div>
            <div>
              <h1 className="font-bold text-gray-900">Contrato de Compra</h1>
              <p className="text-sm text-gray-500">Pedido nº {order?.id}</p>
            </div>
          </div>
          <span className="inline-flex items-center gap-1 text-xs font-semibold border rounded-full px-2.5 py-1 bg-amber-50 text-amber-700 border-amber-200 whitespace-nowrap">
            <i className="ri-time-line"></i>
            Aguardando assinatura
          </span>
        </div>

        {/* Contrato */}
        {contractContent ? (
          <div className="bg-white rounded-2xl border border-gray-200 overflow-hidden mb-4">
            <div className="p-8 [&_*]:max-w-full" dangerouslySetInnerHTML={{ __html: contractContent }} />
          </div>
        ) : (
        <div className="bg-white rounded-2xl border border-gray-200 overflow-hidden mb-4">
          <div className="p-8">
            <h2 className="text-2xl font-bold text-[#E8610A] mb-1">Contrato de Compra e Venda</h2>
            <p className="text-sm text-gray-500 mb-6">Gerado em {formatDate(new Date().toISOString())}</p>

            {/* Dados do cliente */}
            <div className="bg-[#fff7ed] border-l-4 border-[#E8610A] rounded-r-xl p-5 mb-6">
              <h3 className="font-semibold text-gray-900 mb-2 text-sm uppercase tracking-wider">Dados do Cliente</h3>
              <div className="text-sm text-gray-700 space-y-1">
                <p><strong>Nome:</strong> {recipient.name || 'Não informado'}</p>
                {recipient.cnpj && <p><strong>CPF/CNPJ:</strong> {recipient.cnpj}</p>}
                {recipient.phone && <p><strong>Telefone:</strong> {recipient.phone}</p>}
                {recipient.email && <p><strong>E-mail:</strong> {recipient.email}</p>}
                <p><strong>Status do pedido:</strong> {statusLabel}</p>
                {order?.salesperson && <p><strong>Consultor:</strong> {order.salesperson}</p>}
                <p><strong>Data do pedido:</strong> {formatDate(order?.created_at)}</p>
              </div>
            </div>

            {/* Produtos */}
            <h3 className="font-semibold text-gray-900 mb-3 text-sm uppercase tracking-wider">Produtos e Serviços</h3>
            <div className="border border-gray-200 rounded-xl overflow-hidden mb-6">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-gray-50">
                    <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase">Produto</th>
                    <th className="text-right px-4 py-3 text-xs font-semibold text-gray-500 uppercase">Valor</th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((it: any, idx: number) => (
                    <tr key={idx} className="border-t border-gray-100">
                      <td className="px-4 py-3 text-gray-700">
                        <span className="font-semibold text-gray-900">{it.quantity}x</span>{' '}
                        {it.product_name}
                      </td>
                      <td className="px-4 py-3 text-right font-semibold text-gray-900">
                        {formatBRL(Number(it.subtotal ?? it.quantity * it.unit_price))}
                      </td>
                    </tr>
                  ))}
                  <tr className="border-t-2 border-gray-200 bg-gray-50">
                    <td className="px-4 py-3 font-bold text-gray-900">Total</td>
                    <td className="px-4 py-3 text-right font-bold text-lg text-gray-900">{formatBRL(total)}</td>
                  </tr>
                </tbody>
              </table>
            </div>

            {/* Condições */}
            <div className="bg-[#fff7ed] border-l-4 border-[#E8610A] rounded-r-xl p-5 mb-6">
              <h3 className="font-semibold text-gray-900 mb-2 text-sm uppercase tracking-wider">Condições Comerciais</h3>
              <div className="text-sm text-gray-700 space-y-1">
                <p>
                  <strong>Forma de pagamento:</strong>{' '}
                  {order?.payment_terms || <span className="text-gray-400">A definir</span>}
                </p>
                <p>
                  <strong>Prazo de entrega:</strong>{' '}
                  {order?.delivery_deadline || <span className="text-gray-400">A combinar</span>}
                </p>
                <p><strong>Moeda:</strong> Real (R$)</p>
              </div>
            </div>

            {/* Cláusulas */}
            <h3 className="font-semibold text-gray-900 mb-3 text-sm uppercase tracking-wider">Cláusulas do Contrato</h3>
            <ol className="text-sm text-gray-700 space-y-3 mb-6 list-decimal list-inside">
              <li><strong>Objeto:</strong> A empresa <strong>Home Office Premium</strong> se compromete a fornecer os produtos e serviços listados acima, de acordo com as especificações técnicas e quantidades contratadas.</li>
              <li><strong>Pagamento:</strong> O cliente se compromete a realizar o pagamento nas condições acima estabelecidas. Em caso de atraso, serão aplicadas as taxas e correções previstas na legislação vigente.</li>
              <li><strong>Entrega:</strong> O prazo de entrega é estimado conforme condições comerciais. Eventuais atrasos por força maior serão comunicados com antecedência.</li>
              <li><strong>Garantia:</strong> Os produtos possuem garantia contra defeitos de fabricação pelo prazo legal. A garantia não cobre danos por mau uso ou instalação inadequada.</li>
              <li><strong>Cancelamento:</strong> O cancelamento do pedido após aprovação do contrato poderá incorrer em multa proporcional ao estágio da produção, conforme legislação do CDC.</li>
              <li><strong>Propriedade intelectual:</strong> Projetos customizados desenvolvidos especificamente para o cliente são de propriedade do cliente após pagamento integral. Projetos de catálogo permanecem de propriedade da Home Office Premium.</li>
              <li><strong>Responsabilidade:</strong> A empresa se responsabiliza pela qualidade dos produtos e instalação. O cliente deve fornecer acesso e condições adequadas no local de entrega.</li>
              <li><strong>Força maior:</strong> Nenhuma das partes será responsabilizada por atrasos ou falhas decorrentes de eventos de força maior (pandemia, desastres naturais, greves, etc.).</li>
              <li><strong>Lei aplicável:</strong> Este contrato é regido pelas leis da República Federativa do Brasil. Em caso de controvérsia, fica eleito o foro da comarca de Recife/PE.</li>
              <li><strong>Aceitação digital:</strong> Ao aceitar este contrato digitalmente com confirmação de CPF/CNPJ, o cliente declara ter lido, entendido e concordado com todas as cláusulas, conforme art. 107 da Lei nº 14.063/2020.</li>
            </ol>

            <div className="border-t-2 border-gray-200 pt-4 mb-4">
              <p className="text-sm text-gray-600">
                <strong>Home Office Premium</strong> — Móveis corporativos que transformam ambientes<br />
                CNPJ: 00.000.000/0000-00 · Recife/PE
              </p>
            </div>
          </div>
        </div>
        )}

        {/* Área de aceitação */}
        <div className="bg-white rounded-2xl border border-gray-200 p-6 mb-8">
          <h3 className="font-bold text-gray-900 mb-4 flex items-center gap-2">
            <i className="ri-pen-nib-line text-[#E8610A]"></i>
            Assinatura Digital
          </h3>
          <p className="text-sm text-gray-500 mb-4">
            Para aceitar este contrato, informe seu CPF/CNPJ e confirme que leu todas as cláusulas.
            A aceitação tem valor jurídico conforme Lei 14.063/2020.
          </p>

          {submitError && (
            <div className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg px-4 py-3 mb-4">
              {submitError}
            </div>
          )}

          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">
                Nome completo
              </label>
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Seu nome completo"
                className="w-full border border-gray-200 rounded-lg px-4 py-2.5 text-sm focus:outline-none focus:border-[#E8610A]"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">
                CPF ou CNPJ
              </label>
              <input
                type="text"
                value={cpf}
                onChange={(e) => setCpf(formatCPF(e.target.value))}
                placeholder="000.000.000-00"
                maxLength={18}
                className="w-full border border-gray-200 rounded-lg px-4 py-2.5 text-sm focus:outline-none focus:border-[#E8610A]"
              />
              <p className="text-xs text-gray-400 mt-1">Deve ser o mesmo CPF/CNPJ cadastrado no pedido.</p>
            </div>

            <label className="flex items-start gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={accepted}
                onChange={(e) => setAccepted(e.target.checked)}
                className="mt-0.5 w-4 h-4 text-[#E8610A] border-gray-300 rounded focus:ring-[#E8610A]"
              />
              <span className="text-sm text-gray-700">
                Li e aceito todas as cláusulas do contrato acima. Entendo que este documento tem valor jurídico e me comprometo com as condições estabelecidas.
              </span>
            </label>

            <button
              onClick={handleAccept}
              disabled={submitting}
              className="w-full inline-flex items-center justify-center gap-2 bg-[#E8610A] text-white text-sm font-semibold px-6 py-3 rounded-xl hover:bg-[#d45508] transition-colors duration-200 whitespace-nowrap cursor-pointer disabled:opacity-60"
            >
              {submitting ? (
                <>
                  <i className="ri-loader-4-line animate-spin"></i>
                  Processando...
                </>
              ) : (
                <>
                  <i className="ri-check-double-line"></i>
                  Aceitar Contrato
                </>
              )}
            </button>
          </div>
        </div>

        {/* Footer */}
        <div className="text-center text-xs text-gray-400 pb-8">
          <p>Home Office Premium · Contrato gerado automaticamente · {new Date().getFullYear()}</p>
        </div>
      </div>
    </div>
  );
}